import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { useSyncExternalStore } from 'react';

import { describeFailure } from '@/lib/i18n/errors';
import { getPendingSnapshot, subscribePending } from '@/lib/pendingCompletion';
import type { MessageKey } from '@/lib/i18n';
import { tasks } from '@/lib/api/endpoints';
import type {
  OpenStage,
  TaskCreateInput,
  TaskDetail,
  TaskPriority,
  TaskScheduleInput,
  TaskSummary,
  TaskUpdateInput,
  TodayResponse,
} from '@/lib/api/types';
import { bandFor } from '@/lib/priority';
import { keys } from '@/lib/queryClient';
import { useToast } from '@/components/ui/toast';

// --- Queries ----------------------------------------------------------------

export function useToday() {
  return useQuery({ queryKey: keys.today, queryFn: () => tasks.today() });
}

export function useUpcoming(days?: number) {
  return useQuery({
    queryKey: keys.upcoming(days),
    queryFn: () => tasks.upcoming(days),
  });
}

export function useBacklog(offset = 0) {
  return useQuery({
    queryKey: keys.backlog(offset),
    queryFn: () => tasks.backlog({ offset }),
  });
}

/** Work you took part in that is now somebody else's. */
export function useInvolved(offset = 0) {
  return useQuery({
    queryKey: ['tasks', 'involved', offset],
    queryFn: () => tasks.involved({ offset }),
  });
}

export function useCompleted(offset = 0, term = '') {
  // Three characters. Two match so much of a long history that the answer is
  // no more useful than the list it replaced — and the server refuses them.
  const trimmed = term.trim();
  const q = trimmed.length >= 3 ? trimmed : undefined;
  return useQuery({
    queryKey: [...keys.completed(offset), q ?? ''],
    queryFn: () => tasks.completed({ offset }, q),
    // A page of history does not change while it is being read.
    placeholderData: (previous) => previous,
  });
}

export function useAssignedToMe(offset = 0) {
  return useQuery({
    queryKey: keys.assignedToMe(offset),
    queryFn: () => tasks.assignedToMe({ offset }),
  });
}

export function useAssignedByMe(offset = 0) {
  return useQuery({
    queryKey: keys.assignedByMe(offset),
    queryFn: () => tasks.assignedByMe({ offset }),
  });
}

export function useTaskCounts() {
  return useQuery({
    queryKey: keys.counts,
    queryFn: () => tasks.counts(),
    staleTime: 10_000,
  });
}

export function useTask(id: string | null) {
  return useQuery({
    queryKey: keys.task(id ?? ''),
    queryFn: () => tasks.get(id as string),
    enabled: Boolean(id),
  });
}

export function useTaskSearch(term: string) {
  const trimmed = term.trim();
  return useQuery({
    queryKey: keys.search(trimmed),
    queryFn: () => tasks.search(trimmed),
    // The backend requires two characters; asking for fewer is a guaranteed 422.
    enabled: trimmed.length >= 2,
  });
}

// --- Optimistic-update plumbing ---------------------------------------------

/**
 * Every cached shape a task can appear in.
 *
 * An optimistic update has to reach *all* of them, or completing a task on
 * Today would leave it visible in the sidebar count and in Backlog until the
 * next refetch — exactly the flicker optimism is supposed to remove.
 */
type TaskListCache = { items: TaskSummary[]; meta: unknown };

function isTaskListCache(value: unknown): value is TaskListCache {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as TaskListCache).items)
  );
}

function isTodayCache(value: unknown): value is TodayResponse {
  const candidate = value as TodayResponse;
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray(candidate.today) &&
    Array.isArray(candidate.overdue)
  );
}

/**
 * Apply `update` to a task wherever it is cached.
 *
 * Returning `null` from `update` removes the task from lists — which is what
 * completing something on Today means.
 */
function patchTaskEverywhere(
  queryClient: QueryClient,
  taskId: string,
  update: (task: TaskSummary) => TaskSummary | null,
) {
  queryClient.setQueriesData({ queryKey: ['tasks'] }, (cached: unknown) => {
    if (isTodayCache(cached)) {
      const mapList = (list: TaskSummary[]) =>
        list
          .map((task) => (task.id === taskId ? update(task) : task))
          .filter((task): task is TaskSummary => task !== null);
      const today = mapList(cached.today);
      const overdue = mapList(cached.overdue);
      return {
        ...cached,
        today,
        overdue,
        // Completed-today rows stay in the list, so length is not openness.
        open_count: [...today, ...overdue].filter((task) => task.status !== 'COMPLETED')
          .length,
      } satisfies TodayResponse;
    }

    if (isTaskListCache(cached)) {
      return {
        ...cached,
        items: cached.items
          .map((task) => (task.id === taskId ? update(task) : task))
          .filter((task): task is TaskSummary => task !== null),
      };
    }

    if (cached && typeof cached === 'object' && 'groups' in cached) {
      const upcoming = cached as { groups: { date: string; tasks: TaskSummary[] }[] };
      return {
        ...upcoming,
        groups: upcoming.groups
          .map((group) => ({
            ...group,
            tasks: group.tasks
              .map((task) => (task.id === taskId ? update(task) : task))
              .filter((task): task is TaskSummary => task !== null),
          }))
          .filter((group) => group.tasks.length > 0),
      };
    }

    return cached;
  });
}

/**
 * Shared optimistic-mutation shape: snapshot → apply → rollback on failure.
 *
 * The snapshot is of *every* task query, because `patchTaskEverywhere` may have
 * touched any of them. Restoring only the screen in view would leave the others
 * showing the optimistic state after a rollback.
 */
function useOptimisticTaskMutation<TVariables>(options: {
  mutationFn: (variables: TVariables) => Promise<TaskDetail>;
  optimistic: (variables: TVariables) => {
    taskId: string;
    update: (task: TaskSummary) => TaskSummary | null;
  };
  /** Shown when the server rejects the change and the optimistic edit rolls back. */
  failureMessage: MessageKey;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: options.mutationFn,
    onMutate: async (variables) => {
      const { taskId, update } = options.optimistic(variables);

      // Stop in-flight refetches from landing on top of the optimistic state.
      await queryClient.cancelQueries({ queryKey: ['tasks'] });
      const snapshot = queryClient.getQueriesData({ queryKey: ['tasks'] });

      patchTaskEverywhere(queryClient, taskId, update);
      return { snapshot };
    },
    onError: (error, _variables, context) => {
      for (const [key, data] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, data);
      }
      toast.error(describeFailure(error, options.failureMessage));
    },
    onSettled: () => {
      // Reconcile with the server regardless: the optimistic guess covered the
      // fields we changed, not derived ones like sort position.
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

// --- Mutations --------------------------------------------------------------

/** Adds a step to a task, then refreshes that task's detail. */
export function useAddSubtask(parentTaskId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (input: { title: string; assigned_to_user_id?: string }) =>
      tasks.create({ ...input, parent_task_id: parentTaskId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.task(parentTaskId) });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      toast.error(describeFailure(error, 'error.subtaskCreate'));
    },
  });
}

/**
 * Complete or reopen a step.
 *
 * Not one of the optimistic mutations: a step's state changes whether its
 * parent can be completed, and guessing that wrong would leave the parent's
 * checkbox enabled when the server will refuse it.
 */
export function useToggleSubtask(parentTaskId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, completed }: { id: string; completed: boolean }) =>
      completed ? tasks.reopen(id) : tasks.complete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.task(parentTaskId) });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      toast.error(describeFailure(error, 'error.subtaskUpdate'));
    },
  });
}

export function useDeleteSubtask(parentTaskId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => tasks.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.task(parentTaskId) });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      toast.error(describeFailure(error, 'error.subtaskDelete'));
    },
  });
}

export function useReopenTask() {
  return useOptimisticTaskMutation({
    mutationFn: (taskId: string) => tasks.reopen(taskId),
    // Flip in place rather than remove: on Today a completed row lingers, so
    // unticking it should turn it back into an open row right where it is —
    // removing it optimistically made it vanish and reappear a beat later.
    // On the Completed screen the flipped row leaves on the refetch instead,
    // which reads as "returned to work", not "lost".
    optimistic: (taskId) => ({
      taskId,
      update: (task) => ({ ...task, status: 'OPEN' as const, completed_at: null }),
    }),
    failureMessage: 'error.taskReopen',
  });
}

export function useCompleteTask() {
  return useOptimisticTaskMutation({
    mutationFn: (taskId: string) => tasks.complete(taskId),
    // The row does NOT leave the list: completion is confirmed on a row that
    // stays visibly in place, struck through, until the day rolls over. The
    // optimistic update just flips the status; the server payload now keeps
    // completed-today rows in /tasks/today, so the refetch agrees.
    //
    // The undo toast this used to carry is gone with the vanishing: the
    // struck-through row itself is the undo — its checkbox reopens.
    optimistic: (taskId) => ({
      taskId,
      update: (task) => ({
        ...task,
        status: 'COMPLETED' as const,
        completed_at: new Date().toISOString(),
      }),
    }),
    failureMessage: 'error.taskComplete',
  });
}

export function useMoveTask() {
  return useOptimisticTaskMutation({
    mutationFn: ({ taskId, ...body }: { taskId: string } & TaskScheduleInput) =>
      tasks.schedule(taskId, body),
    optimistic: ({ taskId }) => ({
      taskId,
      // The task is leaving this list for another day; which one it lands on is
      // the server's answer, so it is simply removed here and refetched.
      update: () => null,
    }),
    failureMessage: 'error.taskMove',
  });
}

export function useSetPriority() {
  return useOptimisticTaskMutation({
    mutationFn: ({ taskId, priority }: { taskId: string; priority: TaskPriority }) =>
      tasks.update(taskId, { priority }),
    optimistic: ({ taskId, priority }) => ({
      taskId,
      update: (task) => ({ ...task, priority, priority_band: bandFor(priority) }),
    }),
    failureMessage: 'error.priorityChange',
  });
}

/**
 * Move a task between the stages a board shows, from anywhere.
 *
 * Only the two open stages: finishing is `useCompleteTask`, which carries the
 * guard, the steps and the clock. Keeping *done* out of here is what stops
 * this becoming a quiet second way to close something.
 */
export function useSetStage() {
  return useOptimisticTaskMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: OpenStage }) =>
      tasks.update(taskId, { status }),
    optimistic: ({ taskId, status }) => ({
      taskId,
      update: (task) => ({ ...task, status }),
    }),
    failureMessage: 'error.taskUpdate',
  });
}

export function useDeleteTask() {
  return useOptimisticTaskMutation({
    mutationFn: async (taskId: string) => {
      await tasks.remove(taskId);
      return {} as TaskDetail;
    },
    optimistic: (taskId) => ({ taskId, update: () => null }),
    failureMessage: 'error.taskDelete',
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (input: TaskCreateInput) => tasks.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      toast.error(describeFailure(error, 'error.taskCreate'));
    },
  });
}

export function useUpdateTask(taskId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (body: TaskUpdateInput) => tasks.update(taskId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.task(taskId), updated);
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      toast.error(describeFailure(error, 'error.taskUpdate'));
    },
  });
}

/** Move a task under another one, or out from under it. */
export function useSetTaskParent(taskId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (parentTaskId: string | null) => tasks.setParent(taskId, parentTaskId),
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.task(updated.id), updated);
      // Both ends changed shape: the old parent lost a step and the new one
      // gained one, so nothing narrower than the whole tree is safe to keep.
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      toast.error(describeFailure(error, 'error.taskUpdate'));
    },
  });
}

export function useAssignTask(taskId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (assignedToUserId: string) => tasks.assign(taskId, assignedToUserId),
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.task(taskId), updated);
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      toast.error(describeFailure(error, 'error.taskDelegate'));
    },
  });
}

/** The pending-confirmation set, as React state. */
export function usePendingCompletion(taskId: string): boolean {
  return useSyncExternalStore(
    subscribePending,
    () => getPendingSnapshot().has(taskId),
    () => false,
  );
}
